import {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle
} from 'react'
import { useParams } from 'react-router-dom'
import { Box, Typography, IconButton } from '@mui/material'
import EditIcon from '../../images/editIcon.svg'
import { flexcol } from '../../global'
import { seriesEvents, updateEventSeries } from '../../services/EventService'
import EditSeriesEvent from './EditSeriesEvent'
import EditSeriesEventCast from './eventCast/EditSeriesEvent'
import moment from 'moment'
import DeleteEvent from './DeleteEvent'
import { EventType } from '../../services/Types'
import { isPodcastEnabled } from '../../config/featureFlags'

const convertMinutesIntoTime = (time: number) => {
  if (time === 0) {
    return 0
  }
  let period = 'AM ET'
  let hours = Math.floor(time / 60)
  let minutes: any = Math.floor(time % 60)
  if (minutes === 0) {
    minutes = '00'
  }
  if (hours > 11) {
    period = 'PM ET'
  }
  if (hours > 12) {
    hours = hours - 12
  }
  let timeSlot = `${hours}:${minutes} ${period}`
  return timeSlot
}

export const EventList = forwardRef((props: any, ref: any) => {
  useImperativeHandle(ref, () => ({
    updateSeriesList: () => {
      setEventsCount(eventsCount + 1)
    }
  }))

  const increaseEventsCount = () => {
    setEventsCount(eventsCount + 1)
  }

  let { id } = useParams()

  const decreaseEventsCount = async (eventId: number) => {
    if (eventsCount - 1 === 1) await updateEventSeries(Number(id), 0)
    setEventsCount(eventsCount - 1)
    props.removeSerialEvent(eventId)
  }

  const [events, setEvents] = useState<any>([])
  const [seriesId, setSeriesId] = useState(0)
  const [eventsCount, setEventsCount] = useState(0)
  const [selectedEvent, setSelectedEvent] = useState<any>(null)

  const getSeriesEvents = async (eventId: number) => {
    try {
      const { data } = await seriesEvents(eventId)
      if (data) {
        if (data.seriesEvents.statusCode === 200) {
          let allBookingData: any[] = []
          let sId = 0
          data.seriesEvents.data.map((value: any) => {
            let event: any = {
              id: value.id,
              title: value.title,
              start: '',
              startTime: '',
              eventType: value.eventType
            }

            sId = value.seriesId

            if (value.roomBooking.length > 0) {
              let booking = value.roomBooking[0]
              const eventDate = Number(booking.date)
              event.start = moment
                .utc(new Date(eventDate))
                .format('MMMM Do YYYY')
              event.startTime = convertMinutesIntoTime(booking.startTime)
            }

            if (event.eventType !== EventType.SERIES) {
              allBookingData.push(event)
            }
          })
          setSeriesId(sId)
          setEvents(allBookingData)
          setEventsCount(data.seriesEvents.data.length)
        }
      }
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    const loadSeriesEvents = async (eventId: number) => {
      await getSeriesEvents(eventId)
    }
    if (id !== undefined) {
      loadSeriesEvents(Number(id))
    }
  }, [])

  useEffect(() => {
    const loadSeriesEvents = async (eventId: number) => {
      await getSeriesEvents(eventId)
    }
    if (id !== undefined) {
      loadSeriesEvents(Number(id))
    }
  }, [eventsCount])

  const editEventRef = useRef<any>(null)
  const [pendingEdit, setPendingEdit] = useState<{ item: any; index: number } | null>(null)

  const openEditDialog = (item: any, index: number) => {
    setSelectedEvent(item)
    setPendingEdit({ item, index })
  }

  // After selectedEvent causes the dialog component to mount/become available,
  // the ref is populated and we can call updateEvent safely.
  useEffect(() => {
    if (pendingEdit && editEventRef.current) {
      editEventRef.current.updateEvent(pendingEdit.item, pendingEdit.index)
      setPendingEdit(null)
    }
  }, [pendingEdit, selectedEvent])

  return (
    <Box
      style={flexcol}
      sx={{
        rowGap: '8px'
        // maxHeight: '150px',
        // overflow: 'scroll'
      }}
    >
      {events.map((item: any, index: number) => (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row'
          }}
        >
          <Box
            sx={{
              marginRight: '16px',
              width: '337px',
              border: '1px solid rgba(0, 0, 0, 0.03)',
              borderShadow: '0px 2px 12px rgba(0, 0, 0, 0.03)',
              borderRadius: '8px',
              alignSelf: 'stretch',
              padding: '6px',
              paddingLeft: '8px',
              paddingRight: '9px',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <Box
                sx={{
                  width: '32px',
                  height: '32px',
                  minWidth: '32px',
                  minHeight: '32px',
                  borderRadius: '50%',
                  border: '1px solid #E6E1E5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography
                  color='#79747E'
                  sx={{ lineHeight: '24px', fontSize: '16px' }}
                >
                  {index + 1}
                </Typography>
              </Box>

              <Box
                sx={{
                  ml: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: '200px'
                }}
              >
                <Typography
                  textAlign='left'
                  sx={{
                    lineHeight: '18.2px',
                    fontWeight: '500',
                    mb: '2px',
                    width: '200px',
                    wordWrap: 'break-word',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: '2',
                    WebkitBoxOrient: 'vertical'
                  }}
                >
                  {item.title}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row'
                    // justifyContent: 'space-between'
                  }}
                >
                  <Typography
                    sx={{
                      width: '60%',
                      lineHeight: '15.6px',
                      fontWeight: '500',
                      fontSize: '12px',
                      textAlign: 'left',
                      color: '#757575'
                    }}
                  >
                    {String(item.start)}
                  </Typography>
                  <Typography
                    sx={{
                      width: '40%',
                      textAlign: 'left',
                      lineHeight: '15.6px',
                      fontWeight: '400',
                      fontSize: '12px',
                      color: '#757575'
                    }}
                  >
                    {String(item.startTime)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <IconButton style={{ backgroundColor: 'transparent'}} size="large">
              <Box
                component='img'
                onClick={() => {
                  openEditDialog(item, index + 1)
                }}
                src={EditIcon}
                alt='Edit Icon'
              ></Box>
            </IconButton>
          </Box>

          <IconButton style={{ backgroundColor: 'transparent' }} size="large">
            <DeleteEvent
              eventId={item.id}
              decreaseEventsCount={decreaseEventsCount}
              events={events}
              eventsCount={events.length}
            />
          </IconButton>
        </Box>
      ))}
      {selectedEvent?.eventType === 'Webinar' && (
        <EditSeriesEvent
          ref={editEventRef}
          increaseEventsCount={increaseEventsCount}
          saveMainBooking={props.saveMainBooking}
        />
      )}
      {(selectedEvent?.eventType === 'Webcast' || (isPodcastEnabled && selectedEvent?.eventType === 'Podcast')) && (
        <EditSeriesEventCast
          ref={editEventRef}
          increaseEventsCount={increaseEventsCount}
          saveMainBooking={props.saveMainBooking}
        />
      )}
    </Box>
  );
})
